import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  inject,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { SavedCard } from "@models/credit-card";
import {
  InstallmentOption,
  PaymentResponse,
  PaymentResponsePixData,
  PaymentStatusUpdate,
  RemainingPaymentInfo,
  SellerChargePayload,
  SellerPaymentRequest,
} from "@models/payment";
import { AuthService } from "@services/auth.service";
import { CartService } from "@services/cart.service";
import { PaymentNotificationService } from "@services/payment-notification.service";
import { PaymentService } from "@services/payment.service";
import { SnackBarService } from "@services/snack-bar.service";
import { WebSocketService } from "@services/websocket.service";
import { CreditCardWalletComponent } from "@shared/components/credit-card-wallet/credit-card-wallet.component";
import {
  CreditCardComponent,
  CreditCardData,
} from "@shared/components/credit-card/credit-card.component";
import { PaymentProcessingOverlayComponent } from "@shared/components/payment-processing-overlay/payment-processing-overlay.component";
import { ConfirmActionModalComponent } from "@shared/modals/confirm-action-modal/confirm-action-modal.component";
import { QrCodeModule } from "ng-qrcode";
import { provideNgxMask } from "ngx-mask";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { SelectModule } from "primeng/select";
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  exhaustMap,
  finalize,
  interval,
  Observable,
  of,
  Subject,
  Subscription,
  take,
  takeUntil,
  takeWhile,
  tap,
  timer,
} from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { ModalSignupComponent } from "../../../../shared/components/modal-signup/modal-signup.component";
import { CartClientInfo } from "../cart-customer-info-section/cart-customer-info-section.component";
import { MapPixResponsePipe } from "./pipes/map-pix-response.pipe";
import { PixSectionComponent } from "./pix-section/pix-section.component";
import { SellerLinkCheckoutComponent } from "./seller-link-checkout/seller-link-checkout.component";
import {
  SellerPaymentPendingDialogComponent,
  type SellerPaymentPendingDialogData,
} from "./seller-payment-pending-dialog/seller-payment-pending-dialog.component";
import { SellerPixCheckoutComponent } from "./seller-pix-checkout/seller-pix-checkout.component";
import { MarketingPixelService } from "../../../services/marketing/marketing-pixel.service";
import { MarketingJourneyContext } from "../../../services/marketing/marketing.models";
import {
  MINIMUM_PAYMENT_MESSAGE,
  validPaymentAmount,
} from "../../../../shared/helpers/payment-minimum.helper";

type CheckoutPaymentMethod =
  | "pix"
  | "credit_card"
  | "pix_credit_card"
  | "two_credit_cards"
  | "payment_link"
  | "cash";
type CheckoutPaymentMethodChange = "pix" | "credit_card" | "cash";

@Component({
  selector: "app-checkout",
  imports: [
    ReactiveFormsModule,
    CommonModule,
    QrCodeModule,
    ButtonComponent,
    ProgressSpinnerModule,
    SelectModule,
    FormsModule,
    PaymentProcessingOverlayComponent,
    CreditCardComponent,
    CreditCardWalletComponent,
    PixSectionComponent,
    SellerPixCheckoutComponent,
    SellerLinkCheckoutComponent,
    SellerPaymentPendingDialogComponent,
    MapPixResponsePipe,
  ],
  providers: [provideNgxMask({})],
  templateUrl: "./checkout.component.html",
  styleUrl: "./checkout.component.scss",
})
export class CheckoutComponent implements OnInit, OnChanges, OnDestroy {
  private readonly marketingPixels = inject(MarketingPixelService);
  private marketingCheckoutStarted = false;
  @Input() showPaymentMethods = false;
  @Input() salesClosed = false;
  @Input() orderTotal!: number;
  @Input() couponDiscount = 0;
  @Input() sellerId?: string;
  @Input() isSeller = false;
  @Input() eventId?: number;
  @Input() marketingContext?: MarketingJourneyContext;
  @Input() clientInfo: CartClientInfo = {
    client_name: "",
    client_email: "",
    is_foreign_client: false,
    client_country: "BR",
    client_phone_country_code: "+55",
    client_phone: "",
    client_cpf: "",
    client_document: "",
    client_document_type: "cpf",
  };
  @Output() previouSectionClick = new EventEmitter();
  @Output() paymentMethodChange =
    new EventEmitter<CheckoutPaymentMethodChange>();
  @Output() requestClientInfoHighlight = new EventEmitter<void>();
  @Output() installmentChange = new EventEmitter<InstallmentOption | null>();
  // Emitido quando o pagamento é bloqueado por cupom esgotado/expirado (§8),
  // para o carrinho remover o cupom e recalcular o total.
  @Output() couponExpired = new EventEmitter<void>();

  paymentMethod = new FormControl<CheckoutPaymentMethod>("pix");
  cardForm!: FormGroup;
  secondCardForm!: FormGroup;

  statusPaymentPix = "Pendente pagamento";
  checkPaymentInterval?: ReturnType<typeof setInterval>;

  loading = false;
  showAddressFields = false;
  isBrazilianCustomer = true;

  showCreditCardForm = false;
  /** Toggle “Combinar 2 meios” (fase 3 UI). */
  combineTwoMethods = false;
  /** Valor do Pix no split (R$), editável pelo comprador. */
  splitPixAmountReais: number | null = null;
  /** Valor do 1º cartão no split 2 cartões (R$). */
  splitFirstCardAmountReais: number | null = null;
  splitAwaitingPix = false;
  /** 2 cartões: 1º capturado, falta o 2º (retry / estorno). */
  splitAwaitingSecondCard = false;
  remainingPayment: RemainingPaymentInfo | null = null;
  remainingCardProcessing = false;
  savedCards: SavedCard[] = [];
  selectedCardId: string | null = null;
  useNewCard = false;
  selectedSecondCard?: SavedCard;
  useNewSecondCard = false;
  installmentOptions: InstallmentOption[] = [];
  // Opções formatadas para o p-select (label pronto + valor = nº de parcelas).
  installmentSelectOptions: { label: string; value: number }[] = [];
  cardProcessing = false;
  cardStatusMsg = "";
  cardStatusOk = false;

  creditCardData: CreditCardData = {
    cardNumber: "",
    cardHolder: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
    brand: "Visa",
  };
  creditCardDataSecond: CreditCardData = {
    cardNumber: "",
    cardHolder: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
    brand: "Visa",
  };
  isCardFlipped = false;
  isSecondCardFlipped = false;

  currentOrderId: number | null = null;
  sellerPaymentRequestId: number | null = null;

  private paymentStatusSubscription?: Subscription;

  showPaymentOverlay = false;
  currentPaymentStep = "";
  paymentOverlayError = "";

  selectedCard?: SavedCard;
  pixData$?: Observable<PaymentResponse | null>;
  sellerPixData: PaymentResponsePixData | null = null;
  sellerPaymentLinkUrl: string | null = null;
  private readonly sellerPaymentDialogState$ = new BehaviorSubject<{
    statusText: string;
    isWebSocketConnected: boolean;
  }>({
    statusText: "Pendente pagamento",
    isWebSocketConnected: false,
  });
  private sellerPaymentDialogRef: MatDialogRef<SellerPaymentPendingDialogComponent> | null =
    null;
  private sellerDialogWebSocketSub?: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private readonly snackbarService: SnackBarService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private paymentService: PaymentService,
    private paymentNotificationService: PaymentNotificationService,
    private webSocketService: WebSocketService,
    private authService: AuthService,
    private readonly cartService: CartService,
  ) {}

  /** Carrinho gratuito (total R$ 0): finaliza sem passar pelo pagamento. */
  get isFreeOrder(): boolean {
    return (this.orderTotal ?? 0) <= 0;
  }

  finalizeFreeOrder(): void {
    if (this.rejectClosedSale()) return;
    this.loading = true;

    this.cartService
      .purchaseFreeOrder()
      .pipe(
        tap((response) => {
          if (!response?.success) {
            this.snackbarService.openSnackBar(
              response?.error ?? "Não foi possível emitir os ingressos.",
              "Fechar",
              "error",
            );
            return;
          }

          this.router.navigate(["/checkout-success"], {
            queryParams: response.order_id
              ? { orderId: response.order_id }
              : {},
          });
        }),
        catchError((error) => {
          this.snackbarService.openSnackBar(
            error?.error?.error ?? "Erro ao finalizar a compra gratuita.",
            "Fechar",
            "error",
          );
          return EMPTY;
        }),
        finalize(() => (this.loading = false)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.checkUser();

    this.cardForm = this.fb.group({
      holder_name: ["", Validators.required],
      card_number: ["", [Validators.required, Validators.minLength(13)]],
      expiry_month: [
        "",
        [Validators.required, Validators.min(1), Validators.max(12)],
      ],
      expiry_year: ["", [Validators.required]],
      cvv: [
        "",
        [Validators.required, Validators.minLength(3), Validators.maxLength(4)],
      ],
      installments: [1],
    });

    this.secondCardForm = this.fb.group({
      holder_name: ["", Validators.required],
      card_number: ["", [Validators.required, Validators.minLength(13)]],
      expiry_month: [
        "",
        [Validators.required, Validators.min(1), Validators.max(12)],
      ],
      expiry_year: ["", [Validators.required]],
      cvv: [
        "",
        [Validators.required, Validators.minLength(3), Validators.maxLength(4)],
      ],
    });

    const initialMethod: CheckoutPaymentMethod = this.isSeller
      ? this.canUsePix
        ? "pix"
        : "payment_link"
      : this.isBrazilianCustomer
        ? "pix"
        : "credit_card";
    this.paymentMethod.setValue(initialMethod);
    this.emitPaymentMethod(initialMethod);

    this.paymentMethod.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((method) => {
        if (method) {
          this.handlePaymentMethodChange(method);
          this.emitPaymentMethod(method);
        }
      });

    this.cardForm
      .get("installments")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.emitInstallment(value));

    this.handlePaymentMethodChange(this.paymentMethod.value);
    this.loadSavedCards();
  }

  // Emite a opção de parcelamento selecionada para o componente pai (resumo).
  // Emite null quando não é crédito (PIX/link) — sem juros/parcelas no resumo.
  // Multimeios fase 1: sempre 1x no cartão.
  private emitInstallment(installments: number | null): void {
    const isCreditCard = this.paymentMethod.value === "credit_card";
    const option =
      isCreditCard && installments
        ? (this.installmentOptions.find(
            (o) => o.installments === installments,
          ) ?? null)
        : null;
    this.installmentChange.emit(option);
  }

  get isSplitPayment(): boolean {
    const method = this.paymentMethod.value;
    return method === "pix_credit_card" || method === "two_credit_cards";
  }

  get splitCardAmountReais(): number {
    return this.splitRemainingReais;
  }

  get splitPixAmountValid(): boolean {
    return this.splitFirstAmountValid;
  }

  get splitSecondCardAmountReais(): number {
    return this.splitRemainingReais;
  }

  get splitFirstCardAmountValid(): boolean {
    return this.splitFirstAmountValid;
  }

  /** Valor editável do 1º meio (Pix ou 1º cartão). */
  get splitFirstAmountReais(): number | null {
    return this.paymentMethod.value === "two_credit_cards"
      ? this.splitFirstCardAmountReais
      : this.splitPixAmountReais;
  }

  set splitFirstAmountReais(value: number | null) {
    if (this.paymentMethod.value === "two_credit_cards") {
      this.splitFirstCardAmountReais = value;
    } else {
      this.splitPixAmountReais = value;
    }
  }

  get splitRemainingReais(): number {
    const total = Number(this.orderTotal) || 0;
    const first = Number(this.splitFirstAmountReais) || 0;
    return Math.max(0, Math.round((total - first) * 100) / 100);
  }

  get splitFirstAmountValid(): boolean {
    const total = Number(this.orderTotal) || 0;
    const first = Number(this.splitFirstAmountReais) || 0;
    const firstCents = Math.round(first * 100);
    const remainingCents = Math.round(total * 100) - firstCents;
    return validPaymentAmount(first) && firstCents >= 100 && remainingCents >= 100;
  }

  get splitFirstMethodLabel(): string {
    return this.paymentMethod.value === "two_credit_cards" ? "1º cartão" : "Pix";
  }

  get splitSecondMethodLabel(): string {
    return this.paymentMethod.value === "two_credit_cards"
      ? "2º cartão"
      : "cartão";
  }

  /** Valor do Pix pendente após cartão aprovado no split. */
  get splitAwaitingPixAmountReais(): number {
    return Number(this.splitPixAmountReais) || this.splitRemainingReais;
  }

  get remainingPaymentReais(): number {
    const cents = this.remainingPayment?.amount_cents;
    if (cents != null) return cents / 100;
    return this.splitRemainingReais;
  }

  goToMyOrdersPending(): void {
    this.router.navigate(["/my-orders"], {
      queryParams: this.currentOrderId ? { highlight: this.currentOrderId } : {},
    });
  }

  payRemainingWithCard(): void {
    if (!this.currentOrderId || this.remainingCardProcessing) return;
    if (this.rejectBelowMinimum(this.remainingPaymentReais)) return;

    let cardId: string | undefined;
    let cardData: ReturnType<CheckoutComponent["getCardDataFromForm"]> | undefined;

    if (this.selectedSecondCard && !this.useNewSecondCard) {
      cardId = this.selectedSecondCard.id;
    } else {
      this.secondCardForm.markAllAsTouched();
      if (!this.secondCardForm.valid) {
        this.setCardStatus(
          "Preencha os dados do cartão para concluir o valor restante.",
          false,
        );
        return;
      }
      cardData = this.getSecondCardDataFromForm();
    }

    this.remainingCardProcessing = true;
    this.paymentService
      .payRemaining(this.currentOrderId, { cardId, cardData })
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          if (!response.success) {
            throw new Error(
              (response as any).error ||
                response.message ||
                "Falha ao cobrar o restante",
            );
          }
          if ((response as any).remaining_paid || response.payment_status === "paid") {
            this.splitAwaitingSecondCard = false;
            this.remainingPayment = null;
            this.confirmPayment();
            return;
          }
          this.snackbarService.openSnackBar(
            "Cobrança enviada. Aguarde a confirmação.",
            "Fechar",
            "success",
          );
          this.startPixPaymentMonitoring(this.currentOrderId);
        }),
        catchError((error: any) => {
          const msg =
            error?.error?.error ||
            error?.error?.message ||
            error?.message ||
            "Não foi possível concluir o restante. Tente outro cartão ou peça o estorno.";
          this.setCardStatus(msg, false);
          this.snackbarService.openSnackBar(msg, "Fechar", "error");
          return of(null);
        }),
        finalize(() => (this.remainingCardProcessing = false)),
      )
      .subscribe();
  }

  applySplitHalf(): void {
    const total = Number(this.orderTotal) || 0;
    if (total < 2) return;
    this.splitFirstAmountReais = Math.round((total / 2) * 100) / 100;
  }

  applySplitAmount(reais: number): void {
    const total = Number(this.orderTotal) || 0;
    if (total < 2) return;
    const capped = Math.min(Math.max(reais, 1), total - 1);
    this.splitFirstAmountReais = Math.round(capped * 100) / 100;
  }

  toggleCombineTwoMethods(enabled: boolean): void {
    if (this.isSeller) return;
    this.combineTwoMethods = enabled;
    if (!enabled) {
      this.changePaymentMethod(this.canUsePix ? "pix" : "credit_card");
      return;
    }
    const combo: CheckoutPaymentMethod = this.canUsePix
      ? "pix_credit_card"
      : "two_credit_cards";
    this.changePaymentMethod(combo);
  }

  selectSplitCombo(combo: "pix_credit_card" | "two_credit_cards"): void {
    if (combo === "pix_credit_card" && !this.canUsePix) return;
    this.combineTwoMethods = true;
    this.changePaymentMethod(combo);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.showPaymentMethods &&
      !this.isSeller &&
      this.eventId &&
      this.orderTotal > 0 &&
      !this.marketingCheckoutStarted
    ) {
      this.marketingCheckoutStarted = true;
      this.marketingPixels.journey(this.eventId, "checkout_started", {
        ...this.marketingContext,
        currency: this.marketingContext?.currency ?? "BRL",
        // Reuse the buyer's existing coupon-adjusted total, never a new payment calculation.
        value_cents: Math.round(this.orderTotal * 100),
      });
    }
    if (changes["clientInfo"] || changes["isSeller"]) {
      this.ensureAllowedPaymentMethod();
    }
    // Aplicar/remover cupom muda o valor cobrável: rebusca as parcelas (a tabela
    // de juros vem do backend, que já considera o cupom do carrinho).
    if (changes["couponDiscount"] && !changes["couponDiscount"].firstChange) {
      this.refreshInstallmentOptions();
    }
  }

  private refreshInstallmentOptions(): void {
    this.setInstallmentOptions([]);
    if (this.showCreditCardForm) {
      this.loadInstallmentOptions();
      this.emitInstallment(this.cardForm.get("installments")?.value ?? 1);
    }
  }

  // Erro (422) de pagamento por cupom esgotado/expirado — chega como
  // `error.error.error` (HTTP) ou `message` (erro lançado no fluxo).
  private isCouponExpiredError(error: unknown): boolean {
    const err = error as {
      error?: { error?: string; message?: string };
      message?: string;
    };
    const msg = err?.error?.error ?? err?.error?.message ?? err?.message ?? "";
    return typeof msg === "string" && msg.toLowerCase().includes("cupom");
  }

  ngOnDestroy() {
    this.sellerDialogWebSocketSub?.unsubscribe();
    this.sellerPaymentDialogRef?.close();
    this.destroy$.next();
    this.destroy$.complete();
    clearInterval(this.checkPaymentInterval);
    setTimeout(() => this.cleanupWebSocket(), 2000);
  }

  // ---------------------------------------------------------------------------
  // Sessão: Usuário / método de pagamento
  // ---------------------------------------------------------------------------

  private checkUser() {
    if (this.isSeller) {
      this.isBrazilianCustomer = true;
      return;
    }

    const currentUser = this.authService.getLocalStorageUserInfo();
    const address = currentUser?.address;
    const countryCode = (address?.country ?? "BR").toUpperCase();
    if (!address) this.openUserWithoutAddressDialog();
    this.isBrazilianCustomer = countryCode === "BR";
  }

  private handlePaymentMethodChange(method: string | null | undefined) {
    if (!method) return;
    if (this.isSeller) {
      this.showCreditCardForm = false;
      this.splitAwaitingPix = false;
      this.installmentChange.emit(null);
      if (method === "pix" && !this.canUsePix) {
        this.paymentMethod.patchValue("payment_link");
      }
      return;
    }
    if ((method === "pix" || method === "pix_credit_card") && !this.canUsePix) {
      this.paymentMethod.patchValue("credit_card");
      return;
    }
    this.splitAwaitingPix = false;
    this.combineTwoMethods =
      method === "pix_credit_card" || method === "two_credit_cards";
    this.showCreditCardForm =
      method === "credit_card" ||
      method === "pix_credit_card" ||
      method === "two_credit_cards";
    if (method === "pix_credit_card" || method === "two_credit_cards") {
      const total = Number(this.orderTotal) || 0;
      const half = total > 0 ? Math.round((total / 2) * 100) / 100 : null;
      if (method === "pix_credit_card") {
        if (this.splitPixAmountReais == null) this.splitPixAmountReais = half;
      } else {
        if (this.splitFirstCardAmountReais == null) {
          this.splitFirstCardAmountReais = half;
        }
        this.useNewSecondCard = this.savedCards.length === 0;
      }
      this.cardForm.get("installments")?.setValue(1, { emitEvent: false });
      this.installmentChange.emit(null);
      return;
    }
    if (this.showCreditCardForm) {
      this.loadInstallmentOptions();
      this.emitInstallment(this.cardForm.get("installments")?.value ?? 1);
    } else {
      this.installmentChange.emit(null);
    }
  }

  /**
   * Carrega as opções de parcelamento do carrinho atual (tabela de juros vem
   * do backend). Só carrega uma vez por sessão de checkout.
   */
  private loadInstallmentOptions(): void {
    if (this.installmentOptions.length > 0) return;

    this.paymentService
      .getInstallmentOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.setInstallmentOptions(response.installments ?? []);
          if (this.installmentOptions.length > 0) {
            this.cardForm
              .get("installments")
              ?.setValue(this.installmentOptions[0].installments);
          }
        },
        error: () => {
          this.setInstallmentOptions([]);
        },
      });
  }

  private setInstallmentOptions(options: InstallmentOption[]): void {
    this.installmentOptions = options;
    this.installmentSelectOptions = options.map((option) => ({
      label: this.buildInstallmentLabel(option),
      value: option.installments,
    }));
  }

  private buildInstallmentLabel(option: InstallmentOption): string {
    const perInstallment = this.formatBRL(option.installment_cents / 100);
    if (option.interest_free) {
      return `${option.installments}x de ${perInstallment} sem juros`;
    }
    const total = this.formatBRL(option.total_cents / 100);
    return `${option.installments}x de ${perInstallment} com juros (total ${total})`;
  }

  private formatBRL(value: number): string {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }

  goToAddressSection() {
    if (this.isSeller) return;
    this.previouSectionClick.emit();
  }

  private emitPaymentMethod(method: CheckoutPaymentMethod): void {
    // Taxa do split usa tabela de cartão 1x; link de vendedor também conta como crédito.
    const emitted: CheckoutPaymentMethodChange =
      method === "payment_link" ||
      method === "pix_credit_card" ||
      method === "two_credit_cards"
        ? "credit_card"
        : method === "cash"
          ? "cash"
          : method === "pix"
            ? "pix"
            : "credit_card";
    this.paymentMethodChange.emit(emitted);
  }

  private ensureAllowedPaymentMethod(): void {
    if (
      this.isSeller &&
      !this.canUsePix &&
      this.paymentMethod.value === "pix"
    ) {
      this.paymentMethod.patchValue("payment_link");
    }
  }

  private loadSavedCards() {
    this.paymentService
      .getSavedCards()
      .pipe(takeUntil(this.destroy$))
      .subscribe((r) => (this.savedCards = r.cards));
  }

  // ---------------------------------------------------------------------------
  // Sessão: Feedback cartão
  // ---------------------------------------------------------------------------

  private resetCardFeedback() {
    this.cardStatusMsg = "";
    this.cardStatusOk = false;
  }
  private setCardStatus(message: string, ok: boolean) {
    this.cardStatusMsg = message;
    this.cardStatusOk = ok;
  }

  // ---------------------------------------------------------------------------
  // Sessão: Pagamento com cartão (somente Observables)
  // ---------------------------------------------------------------------------

  onCreditCardPay(): void {
    if (this.rejectClosedSale()) return;
    if (this.rejectBelowMinimum()) return;
    if (!this.showCreditCardForm || this.splitAwaitingPix) return;

    const isPixSplit = this.paymentMethod.value === "pix_credit_card";
    const isTwoCards = this.paymentMethod.value === "two_credit_cards";

    if (isPixSplit) {
      if (!this.splitPixAmountValid) {
        this.setCardStatus(
          MINIMUM_PAYMENT_MESSAGE,
          false,
        );
        return;
      }
      if (!this.canGeneratePixQr) {
        this.snackbarService.openSnackBar(
          "O QR Code PIX só é gerado com passaporte cadastrado. Atualize seu cadastro ou use cartão de crédito.",
          "Fechar",
          "error",
        );
        return;
      }
    }

    if (isTwoCards && !this.splitFirstCardAmountValid) {
      this.setCardStatus(
        MINIMUM_PAYMENT_MESSAGE,
        false,
      );
      return;
    }

    const installments =
      isPixSplit || isTwoCards
        ? 1
        : this.cardForm.get("installments")?.value || 1;

    let cardId: string | undefined;
    let cardData: ReturnType<CheckoutComponent["getCardDataFromForm"]> | undefined;
    if (this.selectedCard && !this.useNewCard) {
      cardId = this.selectedCard.id;
    } else {
      this.cardForm.markAllAsTouched();
      if (!this.cardForm.valid) {
        this.setCardStatus(
          "Por favor, preencha todos os campos do primeiro cartão.",
          false,
        );
        return;
      }
      cardData = this.getCardDataFromForm();
    }

    let secondCardId: string | undefined;
    let secondCardData:
      | ReturnType<CheckoutComponent["getCardDataFromForm"]>
      | undefined;
    if (isTwoCards) {
      if (this.selectedSecondCard && !this.useNewSecondCard) {
        if (cardId && this.selectedSecondCard.id === cardId) {
          this.setCardStatus(
            "Selecione dois cartões diferentes para dividir o pagamento.",
            false,
          );
          return;
        }
        secondCardId = this.selectedSecondCard.id;
      } else {
        this.secondCardForm.markAllAsTouched();
        if (!this.secondCardForm.valid) {
          this.setCardStatus(
            "Por favor, preencha todos os campos do segundo cartão.",
            false,
          );
          return;
        }
        secondCardData = this.getSecondCardDataFromForm();
      }
    }

    this.processCreditCard({
      installments,
      cardId,
      cardData,
      secondCardId,
      secondCardData,
    });
  }

  private processCreditCard(params: {
    installments: number;
    cardId?: string | null;
    cardData?: any;
    secondCardId?: string | null;
    secondCardData?: any;
  }): void {
    const isPixSplit = this.paymentMethod.value === "pix_credit_card";
    const isTwoCards = this.paymentMethod.value === "two_credit_cards";
    this.showPaymentOverlay = true;
    this.currentPaymentStep = "processing";
    this.paymentOverlayError = "";
    this.resetCardFeedback();
    this.cardProcessing = true;

    const method = isPixSplit
      ? "pix_credit_card"
      : isTwoCards
        ? "two_credit_cards"
        : "credit_card";

    this.paymentService
      .createPayment(method, {
        cardId: params.cardId || undefined,
        cardData: params.cardData,
        installments: params.installments,
        pixAmountCents: isPixSplit
          ? Math.round(Number(this.splitPixAmountReais) * 100)
          : undefined,
        firstCardAmountCents: isTwoCards
          ? Math.round(Number(this.splitFirstCardAmountReais) * 100)
          : undefined,
        secondCardId: params.secondCardId || undefined,
        secondCardData: params.secondCardData,
      })
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          if (!response.success) {
            throw new Error(response.message || "Falha ao processar pagamento");
          }
          this.currentOrderId = response.order_id || null;

          if (isPixSplit) {
            // Cartão cobrado; se houver QR, mostra Pix. Se o Pix falhou na geração,
            // pedido parcial fica em Meus Pedidos (concluir restante / estorno).
            this.showPaymentOverlay = false;
            this.splitAwaitingPix = true;
            this.showCreditCardForm = false;
            this.remainingPayment = response.remaining_payment ?? null;
            const hasPixQr = !!(
              response.pix_data?.qr_code ||
              (response as { qr_code?: string }).qr_code
            );
            this.pixData$ = of(response);
            if (hasPixQr) {
              this.startPixPaymentMonitoring(response.order_id || null);
            }
            this.snackbarService.openSnackBar(
              response.remaining_payment?.message ||
                (hasPixQr
                  ? "Cartão aprovado. Pague o Pix para concluir o pedido."
                  : "Pagamento parcial. Conclua o restante ou peça o estorno em Meus Pedidos."),
              "Fechar",
              "success",
            );
            return;
          }

          // 2 cartões: 1º ok e 2º falhou/pendente → pedido pendente (estilo ML).
          if (isTwoCards && response.remaining_payment?.amount_cents) {
            this.showPaymentOverlay = false;
            this.showCreditCardForm = false;
            this.splitAwaitingSecondCard = true;
            this.remainingPayment = response.remaining_payment;
            this.snackbarService.openSnackBar(
              response.remaining_payment.message ||
                "Parte paga. Conclua com outro cartão ou peça o estorno.",
              "Fechar",
              "success",
            );
            return;
          }

          this.confirmPayment();
        }),
        catchError((error: any) => {
          const msg =
            error?.error?.message ||
            error?.error?.error ||
            "Erro ao processar pagamento";
          this.paymentOverlayError = msg;
          this.setCardStatus(msg, false);

          if (this.isCouponExpiredError(error)) {
            this.couponExpired.emit();
          }

          const missing = error?.error?.missing_fields as string[] | undefined;
          if (missing?.length) {
            const missingFields = missing.join(", ");
            this.snackbarService.openSnackBar(
              error.error.error ??
                `Dados incompletos: ${missingFields}. Por favor, atualize seu cadastro.`,
              "Fechar",
              "error",
            );
            this.router.navigate(["/finish-signup"]);
          }
          return of(null);
        }),
        finalize(() => {
          this.cardProcessing = false;
        }),
      )
      .subscribe();
  }

  private getCardDataFromForm() {
    return this.extractCardDataFromForm(this.cardForm);
  }

  private getSecondCardDataFromForm() {
    return this.extractCardDataFromForm(this.secondCardForm);
  }

  private extractCardDataFromForm(form: FormGroup) {
    const num = form.get("card_number")?.value?.replace(/\s/g, "") ?? "";
    const month = String(form.get("expiry_month")?.value ?? "").padStart(2, "0");
    const rawYear = String(form.get("expiry_year")?.value ?? "");
    const year =
      rawYear.length === 2
        ? String(new Date().getFullYear()).slice(0, 2) + rawYear
        : rawYear;

    return {
      card_number: num,
      card_holder_name: form.get("holder_name")?.value ?? "",
      card_expiration_date: `${month}${year.slice(-2)}`, // MMYY
      card_cvv: form.get("cvv")?.value ?? "",
    };
  }

  private confirmPayment(): void {
    this.currentPaymentStep = "finalizing";
    if (this.currentOrderId) {
      this.waitForWebSocketConfirmation(this.currentOrderId);
    } else {
      this.proceedToSuccess();
    }
  }

  // ---------------------------------------------------------------------------
  // Sessão: PIX (somente Observables)
  // ---------------------------------------------------------------------------

  generatePixPayment(): void {
    if (this.rejectClosedSale()) return;
    if (this.rejectBelowMinimum()) return;
    if (!this.canUsePix) return;
    if (!this.canGeneratePixQr) {
      this.snackbarService.openSnackBar(
        "O QR Code PIX só é gerado com passaporte cadastrado. Atualize seu cadastro ou use cartão de crédito.",
        "Fechar",
        "error",
      );
      return;
    }
    this.loading = true;

    this.pixData$ = this.paymentService.createPayment("pix").pipe(
      tap((response) => {
        if (response?.error) {
          if (this.isCouponExpiredError({ error: response })) {
            this.couponExpired.emit();
          }
          this.snackbarService.openSnackBar(
            this.translateErrorMessage(response.message ?? response.error),
            "Fechar",
            "error",
          );
        } else {
          this.startPixPaymentMonitoring(response.order_id || null);
        }
      }),
      catchError((error) => this.handleGeneratePixPaymentError(error)),
      finalize(() => (this.loading = false)),
      takeUntil(this.destroy$),
    );
  }

  private handleGeneratePixPaymentError(error: HttpErrorResponse) {
    if (this.isCouponExpiredError(error)) {
      this.couponExpired.emit();
    }
    if (error?.error?.error?.error) {
      this.snackbarService.openSnackBar(
        "Erro ao finalizar compra " + error?.error?.error?.error,
        "Fechar",
        "error",
      );

      const msg = String(error?.error?.error?.error);
      if (msg.includes("cpf") || msg.includes("CPF")) this.openModalSignUp();
    } else if (error?.error?.missing_fields) {
      const missing = error.error.missing_fields.join(", ");
      this.snackbarService.openSnackBar(
        error.error.error ??
          `Dados incompletos: ${missing}. Por favor, atualize seu cadastro.`,
        "Fechar",
        "error",
      );
      this.router.navigate(["/finish-signup"]);
    } else {
      this.snackbarService.openSnackBar(
        this.translateErrorMessage(
          error?.error?.message ?? error?.error?.error,
        ),
        "Fechar",
        "error",
      );
    }
    return of(null);
  }

  private startPixPaymentMonitoring(
    orderId: number | null,
    sellerOpts?: { sellerPaymentRequestId: number },
  ): void {
    this.statusPaymentPix = "AGUARDANDO PAGAMENTO";

    if (!this.isSeller) {
      if (!orderId) return;
      this.currentOrderId = orderId;
      this.sellerPaymentRequestId = null;
    } else {
      if (!sellerOpts?.sellerPaymentRequestId) return;
      this.sellerPaymentRequestId = sellerOpts.sellerPaymentRequestId;
      this.currentOrderId = orderId;
      const sprId = sellerOpts.sellerPaymentRequestId;
      if (!this.webSocketService.isConnected()) {
        this.webSocketService
          .connect()
          .pipe(takeUntil(this.destroy$))
          .subscribe((state) => {
            if (state.connected) {
              this.subscribeToPixPaymentSeller(sprId);
            } else if (state.error) {
              this.fallbackToPolling();
            }
          });
      } else {
        this.subscribeToPixPaymentSeller(sprId);
      }
      return;
    }

    const oid = orderId!;

    if (!this.webSocketService.isConnected()) {
      this.webSocketService
        .connect()
        .pipe(takeUntil(this.destroy$))
        .subscribe((state) => {
          if (state.connected) {
            this.subscribeToPixPayment(oid);
          } else if (state.error) {
            this.fallbackToPolling();
          }
        });
    } else {
      this.subscribeToPixPayment(oid);
    }
  }

  private subscribeToPixPaymentSeller(sellerPaymentRequestId: number): void {
    const monitoring$ =
      this.paymentNotificationService.startSellerPaymentRequestMonitoring(
        sellerPaymentRequestId,
      );

    this.paymentStatusSubscription = monitoring$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (u: PaymentStatusUpdate) => this.handlePixPaymentUpdate(u),
        error: () => this.fallbackToPolling(),
      });
  }

  private subscribeToPixPayment(orderId: number): void {
    const monitoring$ =
      this.paymentNotificationService.startOrderMonitoring(orderId);

    this.paymentStatusSubscription = monitoring$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (u: PaymentStatusUpdate) => this.handlePixPaymentUpdate(u),
        error: () => this.fallbackToPolling(),
      });
  }

  private handlePixPaymentUpdate(update: PaymentStatusUpdate): void {
    const st = update.status?.toLowerCase() ?? "";
    const orderIdForNav = update.order_id || update.data?.order_id;
    if (st === "succeeded" || st === "paid") {
      this.statusPaymentPix = "PAGO";
      this.updateSellerDialogState();
      this.cleanupWebSocketSubscriptions();
      if (this.isSeller) {
        this.sellerPaymentDialogRef?.close();
      }
      setTimeout(() => {
        this.router.navigate(["/checkout-success"], {
          queryParams: orderIdForNav ? { orderId: orderIdForNav } : {},
        });
      }, 1000);
    } else if (
      st === "failed" ||
      st === "cancelled" ||
      st === "canceled" ||
      st === "expired"
    ) {
      this.statusPaymentPix = "FALHOU";
      this.updateSellerDialogState();
      this.snackbarService.openSnackBar(
        `Pagamento PIX falhou: ${update.data?.failure_reason || "Erro desconhecido"}`,
        "Fechar",
        "error",
      );
      this.cleanupWebSocketSubscriptions();
    } else {
      this.statusPaymentPix = "AGUARDANDO PAGAMENTO";
      this.updateSellerDialogState();
    }
  }

  private fallbackToPolling(): void {
    if (this.isSeller) {
      if (!this.sellerPaymentRequestId) return;

      this.snackbarService.openSnackBar(
        "Conexão em tempo real indisponível. Verificando pagamento periodicamente…",
        "Fechar",
        "warning",
      );

      timer(0, 5000)
        .pipe(
          exhaustMap(() =>
            this.paymentService
              .refreshSellerPaymentRequest(this.sellerPaymentRequestId!)
              .pipe(catchError(() => EMPTY)),
          ),
          takeWhile(
            (req) =>
              !this.isSellerPaymentTerminalSuccess(req) &&
              !this.isSellerPaymentTerminalFailure(req),
            true,
          ),
          tap((req) => this.handleSellerPaymentRequestRefresh(req)),
          takeUntil(this.destroy$),
        )
        .subscribe();
      return;
    }

    if (!this.currentOrderId) return;

    interval(5000)
      .pipe(
        // evita sobreposição de chamadas caso a API demore > 5s
        exhaustMap(() =>
          this.paymentService.checkPaymentStatus(this.currentOrderId!).pipe(
            // se der erro, só ignora este tick e continua polling
            catchError(() => EMPTY),
          ),
        ),
        // emite até encontrar sucesso; `true` inclui a última emissão (de sucesso)
        takeWhile(
          (resp) => resp.status !== "succeeded" && resp.status !== "paid",
          true,
        ),
        tap((resp) => {
          if (resp.status === "succeeded" || resp.status === "paid") {
            this.statusPaymentPix = "PAGO";
            this.router.navigate(["/checkout-success"], {
              queryParams: { orderId: this.currentOrderId },
            });
          }
        }),
        // garante cancelamento no destroy do componente
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private isSellerPaymentTerminalSuccess(req: SellerPaymentRequest): boolean {
    const s = req.status?.toLowerCase() ?? "";
    return s === "succeeded" || s === "paid";
  }

  private isSellerPaymentTerminalFailure(req: SellerPaymentRequest): boolean {
    const s = req.status?.toLowerCase() ?? "";
    return (
      s === "failed" || s === "canceled" || s === "cancelled" || s === "expired"
    );
  }

  private handleSellerPaymentRequestRefresh(req: SellerPaymentRequest): void {
    if (this.isSellerPaymentTerminalSuccess(req)) {
      this.handlePixPaymentUpdate({
        type: "payment_status_update",
        order_id: req.order_id ?? 0,
        status: req.status,
        data: {
          payment_method: "",
          amount: 0,
          timestamp: "",
          correlation_id: "",
        },
      });
      return;
    }
    if (this.isSellerPaymentTerminalFailure(req)) {
      this.handlePixPaymentUpdate({
        type: "payment_status_update",
        order_id: req.order_id ?? 0,
        status: req.status,
        data: {
          payment_method: "",
          amount: 0,
          timestamp: "",
          correlation_id: "",
        },
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Sessão: WebSocket e status
  // ---------------------------------------------------------------------------

  private waitForWebSocketConfirmation(orderId: number): void {
    if (!this.webSocketService.isConnected()) {
      this.webSocketService
        .connect()
        .pipe(takeUntil(this.destroy$))
        .subscribe((ws) => {
          if (ws.connected) {
            this.startPaymentMonitoring(orderId);
          } else {
            this.handleWebSocketError();
          }
        });
    } else {
      this.startPaymentMonitoring(orderId);
    }
  }

  private startPaymentMonitoring(orderId: number): void {
    const timeout: ReturnType<typeof setTimeout> = setTimeout(
      () => this.handleWebSocketTimeout(),
      45000,
    );

    this.paymentStatusSubscription = this.paymentNotificationService
      .startOrderMonitoring(orderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (u) => this.handlePaymentStatusUpdate(u, timeout),
        error: () => {
          clearTimeout(timeout);
          this.handleWebSocketError();
        },
      });
  }

  private handlePaymentStatusUpdate(
    update: PaymentStatusUpdate,
    timeout: ReturnType<typeof setTimeout>,
  ): void {
    clearTimeout(timeout);

    if (
      (update.type === "payment_confirmed" && update.status === "succeeded") ||
      (update.type === "payment_status_update" && update.status === "paid")
    ) {
      this.setCardStatus(
        "Pagamento confirmado pelo servidor! Finalizando...",
        true,
      );
      this.proceedToSuccess();
    } else if (update.type === "payment_failed" || update.status === "failed") {
      this.handlePaymentFailure(
        update.data?.failure_reason || "Erro desconhecido",
      );
    } else {
      this.setCardStatus(`Status: ${update.status}...`, true);
    }
  }

  private handleWebSocketTimeout(): void {
    this.setCardStatus("Pagamento processado! Verificando status...", true);
    if (this.currentOrderId) {
      this.checkPaymentStatusFallback();
    } else {
      this.proceedToSuccess();
    }
  }

  private handleWebSocketError(): void {
    this.setCardStatus("Verificando pagamento...", true);
    if (this.currentOrderId) {
      this.checkPaymentStatusFallback();
    } else {
      this.proceedToSuccess();
    }
  }

  private checkPaymentStatusFallback(): void {
    if (!this.currentOrderId) {
      this.proceedToSuccess();
      return;
    }

    this.paymentService
      .checkPaymentStatus(this.currentOrderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.proceedToSuccess(),
        error: () => this.proceedToSuccess(),
      });
  }

  private proceedToSuccess(): void {
    setTimeout(() => {
      this.showPaymentOverlay = false;
      this.setCardStatus(
        "🎉 Pagamento realizado com sucesso! Redirecionando...",
        true,
      );
      // this.clearCartAfterPayment();
      setTimeout(() => {
        this.router.navigate(["/checkout-success"], {
          queryParams: { orderId: this.currentOrderId },
        });
      }, 2000);
    }, 1500);
  }

  private handlePaymentFailure(reason: string): void {
    this.showPaymentOverlay = false;
    this.setCardStatus(`Pagamento falhou: ${reason}`, false);
    this.cleanupWebSocketSubscriptions();
  }

  private cleanupWebSocketSubscriptions(): void {
    this.paymentStatusSubscription?.unsubscribe();
    this.paymentStatusSubscription = undefined;
  }

  private cleanupWebSocket(): void {
    this.cleanupWebSocketSubscriptions();
    setTimeout(
      () => this.paymentNotificationService.stopOrderMonitoring(),
      3000,
    );
  }

  // ---------------------------------------------------------------------------
  // Sessão: Outros utilitários / template
  // ---------------------------------------------------------------------------

  translateErrorMessage(error: string) {
    if (/Quantity of ticket '.*?' must be between \d+ and \d+\./.test(error)) {
      return "A quantidade do ingresso deve estar dentro do intervalo permitido.";
    }
    return error === "Quantity Insufficient tickets available for this batch"
      ? "Quantidade insuficiente de ingressos disponíveis para este lote."
      : error;
  }

  onCreditCardDataChange(newData: CreditCardData) {
    this.creditCardData = newData;
    this.patchCardFormFromUi(this.cardForm, newData);
  }

  onSecondCreditCardDataChange(newData: CreditCardData) {
    this.creditCardDataSecond = newData;
    this.patchCardFormFromUi(this.secondCardForm, newData);
  }

  private patchCardFormFromUi(form: FormGroup, newData: CreditCardData) {
    const year =
      newData.expiryYear?.length === 2
        ? String(new Date().getFullYear()).slice(0, 2) + newData.expiryYear
        : newData.expiryYear;

    const cleanCardNumber = newData.cardNumber?.replace(/\s/g, "") ?? "";

    form.patchValue(
      {
        holder_name: newData.cardHolder,
        card_number: cleanCardNumber,
        expiry_month: newData.expiryMonth,
        expiry_year: year,
        cvv: newData.cvv,
      },
      { emitEvent: false },
    );
    form.updateValueAndValidity({ emitEvent: false });
  }

  onCardFlip() {
    this.isCardFlipped = !this.isCardFlipped;
  }
  onCvvFocus() {
    this.isCardFlipped = true;
  }
  onSecondCardFlip() {
    this.isSecondCardFlipped = !this.isSecondCardFlipped;
  }
  onSecondCvvFocus() {
    this.isSecondCardFlipped = true;
  }

  changePaymentMethod(newValue: CheckoutPaymentMethod) {
    if (
      (newValue === "pix" || newValue === "pix_credit_card") &&
      !this.canUsePix
    ) {
      return;
    }
    if (newValue === "pix" || newValue === "credit_card") {
      this.combineTwoMethods = false;
    }
    const previous = this.paymentMethod.value;
    if (this.isSeller && previous !== newValue) {
      this.sellerDialogWebSocketSub?.unsubscribe();
      this.sellerPaymentDialogRef?.close();
      this.sellerPixData = null;
      this.sellerPaymentLinkUrl = null;
      this.statusPaymentPix = "Pendente pagamento";
    }
    this.paymentMethod.patchValue(newValue);
  }

  onSubmit(): void {
    if (this.rejectClosedSale()) return;
    if (this.isSeller) {
      this.onSellerSubmit();
      return;
    }

    if (this.showCreditCardForm) {
      this.onCreditCardPay();
      return;
    }
    this.loading = true;

    if (this.paymentMethod.value === "pix") {
      this.generatePixPayment();
    } else if (this.paymentMethod.value === "credit_card") {
      this.onCreditCardPay();
    } else {
      this.snackbarService.openSnackBar(
        "Formulário inválido!",
        "Fechar",
        "error",
      );
      this.loading = false;
    }
  }

  onSellerSubmit(): void {
    if (this.rejectClosedSale()) return;
    if (this.paymentMethod.value !== "cash" && this.rejectBelowMinimum()) return;
    if (this.paymentMethod.value === "pix") {
      if (!this.canUsePix) return;
      this.generateSellerPixCharge();
      return;
    }

    if (this.paymentMethod.value === "payment_link") {
      this.generateSellerPaymentLink();
      return;
    }

    if (this.paymentMethod.value === "cash") {
      this.confirmSellerCashSale();
      return;
    }

    this.snackbarService.openSnackBar(
      "Selecione um método válido para o vendedor.",
      "Fechar",
      "error",
    );
  }

  private rejectBelowMinimum(reais = Number(this.orderTotal)): boolean {
    if (validPaymentAmount(reais)) return false;
    this.loading = false;
    this.setCardStatus(MINIMUM_PAYMENT_MESSAGE, false);
    this.snackbarService.openSnackBar(MINIMUM_PAYMENT_MESSAGE, "Fechar", "error");
    return true;
  }

  private openSellerPaymentDialog(mode: "pix" | "payment_link"): void {
    if (!this.isSeller) return;
    this.sellerDialogWebSocketSub?.unsubscribe();

    if (this.sellerPaymentDialogRef) {
      this.sellerPaymentDialogRef.close();
    }

    this.sellerPaymentDialogState$.next({
      statusText: this.statusPaymentPix,
      isWebSocketConnected: this.webSocketService.isConnected(),
    });

    const data: SellerPaymentPendingDialogData = {
      mode,
      pixData: this.sellerPixData,
      paymentLinkUrl: this.sellerPaymentLinkUrl,
      state$: this.sellerPaymentDialogState$,
    };

    const ref = this.dialog.open(SellerPaymentPendingDialogComponent, {
      panelClass: "seller-payment-pending-dialog-panel",
      width: "min(100vw - 1.5rem, 32rem)",
      maxWidth: "100vw",
      disableClose: true,
      autoFocus: "first-tabbable",
      data,
    });
    this.sellerPaymentDialogRef = ref;

    this.sellerDialogWebSocketSub = this.webSocketService
      .getConnectionState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((s) => {
        this.sellerPaymentDialogState$.next({
          statusText: this.statusPaymentPix,
          isWebSocketConnected: s.connected,
        });
      });

    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.sellerPaymentDialogRef === ref) {
          this.sellerDialogWebSocketSub?.unsubscribe();
          this.sellerDialogWebSocketSub = undefined;
          this.sellerPaymentDialogRef = null;
        }
      });
  }

  private updateSellerDialogState(): void {
    if (!this.sellerPaymentDialogRef) return;
    this.sellerPaymentDialogState$.next({
      statusText: this.statusPaymentPix,
      isWebSocketConnected: this.webSocketService.isConnected(),
    });
  }

  private buildSellerPayload(): SellerChargePayload | null {
    if (!this.eventId) {
      this.snackbarService.openSnackBar(
        "Não foi possível identificar o evento para gerar a cobrança.",
        "Fechar",
        "error",
      );
      return null;
    }

    const clientName = this.clientInfo.client_name?.trim();
    const clientEmail = this.clientInfo.client_email?.trim();
    const clientPhoneCountryCode =
      this.clientInfo.client_phone_country_code?.trim();
    const clientPhone = this.clientInfo.client_phone?.trim();
    const clientCpf = this.clientInfo.client_cpf?.trim();
    const clientDocument = this.clientInfo.client_document?.trim();
    const clientCountry = this.clientInfo.client_country?.trim();

    if (
      !clientName ||
      !clientEmail ||
      !clientPhoneCountryCode ||
      !clientPhone
    ) {
      this.requestClientInfoHighlight.emit();
      this.snackbarService.openSnackBar(
        "Preencha os dados do cliente antes de gerar a cobrança.",
        "Fechar",
        "error",
      );
      return null;
    }

    const commonPayload = {
      event_id: this.eventId,
      client_email: clientEmail,
      client_phone_country_code: this.normalizePhoneCountryCode(
        clientPhoneCountryCode,
      ),
      client_phone: clientPhone,
      client_name: clientName,
    };

    if (this.clientInfo.is_foreign_client) {
      if (!clientDocument || !clientCountry) {
        this.requestClientInfoHighlight.emit();
        this.snackbarService.openSnackBar(
          "Preencha os dados do cliente antes de gerar a cobrança.",
          "Fechar",
          "error",
        );
        return null;
      }

      return {
        ...commonPayload,
        client_document: clientDocument,
        client_document_type: "passport",
        client_country: clientCountry,
      };
    }

    if (!clientCpf) {
      this.requestClientInfoHighlight.emit();
      this.snackbarService.openSnackBar(
        "Preencha os dados do cliente antes de gerar a cobrança.",
        "Fechar",
        "error",
      );
      return null;
    }

    return {
      ...commonPayload,
      client_cpf: clientCpf,
    };
  }

  private normalizePhoneCountryCode(phoneCountryCode: string): string {
    return phoneCountryCode.replace(/^\+/, "");
  }

  generateSellerPixCharge(): void {
    if (this.rejectClosedSale()) return;
    if (!this.canGeneratePixQr) {
      this.snackbarService.openSnackBar(
        "O QR Code PIX só é gerado se o cliente tiver passaporte cadastrado. Use o link de cartão.",
        "Fechar",
        "error",
      );
      return;
    }
    const payload = this.buildSellerPayload();
    if (!payload) return;

    this.loading = true;
    this.sellerPaymentLinkUrl = null;

    this.paymentService
      .createSellerPixCharge(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (response) => {
          if (!this.isSellerPixChargeValid(response.pix_data)) {
            this.sellerPixData = null;
            this.statusPaymentPix = "FALHOU";
            this.snackbarService.openSnackBar(
              "Não foi possível gerar o QR Code PIX. Tente novamente.",
              "Fechar",
              "error",
            );
            return;
          }

          this.sellerPixData = response.pix_data;
          this.startPixPaymentMonitoring(response.order_id ?? null, {
            sellerPaymentRequestId: response.seller_payment_request_id,
          });
          this.openSellerPaymentDialog("pix");
        },
        error: (error) => {
          this.snackbarService.openSnackBar(
            error?.error?.message || "Erro ao gerar cobrança PIX.",
            "Fechar",
            "error",
          );
        },
      });
  }

  generateSellerPaymentLink(): void {
    if (this.rejectClosedSale()) return;
    const payload = this.buildSellerPayload();
    if (!payload) return;

    this.loading = true;
    this.sellerPixData = null;

    this.paymentService
      .createSellerPaymentLink(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (response) => {
          if (!response.payment_link_url) {
            this.sellerPaymentLinkUrl = null;
            this.statusPaymentPix = "FALHOU";
            this.snackbarService.openSnackBar(
              "Não foi possível gerar o link de pagamento. Tente novamente.",
              "Fechar",
              "error",
            );
            return;
          }

          this.sellerPaymentLinkUrl = response.payment_link_url;
          this.startPixPaymentMonitoring(response.order_id ?? null, {
            sellerPaymentRequestId: response.seller_payment_request_id,
          });
          this.openSellerPaymentDialog("payment_link");
        },
        error: (error) => {
          this.snackbarService.openSnackBar(
            error?.error?.message || "Erro ao gerar link de pagamento.",
            "Fechar",
            "error",
          );
        },
      });
  }

  confirmSellerCashSale(): void {
    const dialogRef = this.dialog.open(ConfirmActionModalComponent, {
      width: "min(100vw - 1.5rem, 28rem)",
      maxWidth: "100vw",
      autoFocus: "first-tabbable",
      restoreFocus: true,
      data: {
        modalTitle: "Confirma o recebimento em dinheiro?",
        modalContent:
          "Confirme somente se o valor em dinheiro já foi recebido. Após confirmar, a venda será registrada como paga.",
        confirmMessage: "Sim, registrar venda",
        cancelMessage: "Cancelar",
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.generateSellerCashSale();
        }
      });
  }

  generateSellerCashSale(): void {
    if (this.rejectClosedSale()) return;
    const payload = this.buildSellerPayload();
    if (!payload) return;

    this.loading = true;
    this.sellerPixData = null;
    this.sellerPaymentLinkUrl = null;

    this.paymentService
      .createSellerCashSale(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (response) => {
          this.statusPaymentPix = "PAGO";
          this.sellerPaymentRequestId =
            response.seller_payment_request_id ?? null;
          this.cleanupWebSocketSubscriptions();
          this.sellerPaymentDialogRef?.close();
          this.snackbarService.openSnackBar(
            "Venda em dinheiro registrada com sucesso.",
            "Fechar",
            "success",
          );
          this.router.navigate(["/checkout-success"], {
            queryParams: response.order_id
              ? { orderId: response.order_id }
              : {},
          });
        },
        error: (error) => {
          this.statusPaymentPix = "FALHOU";
          this.snackbarService.openSnackBar(
            error?.error?.message || "Erro ao registrar venda em dinheiro.",
            "Fechar",
            "error",
          );
        },
      });
  }

  /**
   * Enquanto houver cobrança seller ativa, não repete QR/link/copia e cola na seção
   * principal (eles ficam no painel). Em falha, a seção mostra tudo de novo p/ retentar.
   */
  get suppressSellerInlinePaymentDetails(): boolean {
    if (!this.isSeller) return false;
    if (this.statusPaymentPix === "FALHOU") return false;
    return this.sellerPixData != null || this.sellerPaymentLinkUrl != null;
  }

  reopenSellerPaymentDialog(): void {
    if (this.paymentMethod.value === "pix" && this.sellerPixData) {
      this.openSellerPaymentDialog("pix");
    } else if (
      this.paymentMethod.value === "payment_link" &&
      this.sellerPaymentLinkUrl
    ) {
      this.openSellerPaymentDialog("payment_link");
    }
  }

  private isSellerPixChargeValid(
    pixData: PaymentResponsePixData | null,
  ): boolean {
    if (!pixData) return false;
    if (pixData.status === "failed") return false;
    return !!pixData.qr_code;
  }

  openModalSignUp() {
    this.dialog.open(ModalSignupComponent, {
      width: "800px",
      maxWidth: "95vw",
    });
  }

  openUserWithoutAddressDialog() {
    const dialogRef = this.dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: "Cadastro incompleto",
        modalContent: `Para finalizar sua compra você precisa finalizar completamente seu cadastro`,
        confirmMessage: "Confirmar",
        cancelMessage: "",
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this.openModalSignUp());
  }

  shouldShowProcessingIndicator(): boolean {
    return this.cardProcessing || this.loading;
  }
  getPaymentStatusText(): string {
    return this.cardProcessing
      ? "Processando pagamento..."
      : this.loading
        ? "Carregando..."
        : "";
  }
  shouldShowSuccessMessage(): boolean {
    return this.cardStatusOk && this.cardStatusMsg.includes("aprovado");
  }
  shouldShowErrorMessage(): boolean {
    return !this.cardStatusOk && this.cardStatusMsg.length > 0;
  }
  get paymentStatusMessage(): string {
    return this.cardStatusMsg;
  }
  get overlayPaymentMethod(): "pix" | "credit_card" {
    return this.paymentMethod.value === "pix" ? "pix" : "credit_card";
  }
  get isWebSocketConnected(): boolean {
    return this.webSocketService.isConnected();
  }

  get canUsePix(): boolean {
    return true;
  }

  private rejectClosedSale(): boolean {
    if (!this.salesClosed) return false;
    this.loading = false;
    this.snackbarService.openSnackBar('As vendas online foram encerradas no início do evento.', 'Fechar', 'error');
    return true;
  }

  get canGeneratePixQr(): boolean {
    if (this.isSeller) {
      if (!this.clientInfo.is_foreign_client) {
        return true;
      }
      return this.hasPassportDocument(this.clientInfo.client_document);
    }

    if (this.isBrazilianCustomer) {
      return true;
    }

    const currentUser = this.authService.getLocalStorageUserInfo();
    return this.hasPassportDocument(
      currentUser?.identity_card ?? currentUser?.passport_number,
    );
  }

  private hasPassportDocument(value?: string | null): boolean {
    return (value ?? "").trim().length >= 5;
  }

  get isSellerPaymentModalOpen(): boolean {
    return this.sellerPaymentDialogRef != null;
  }
}
